/**
 * @file PlacementRows.tsx
 * @description Список выбранных партий внутри шторки размещения
 * (phase19.24 этап 1, шаг 4).
 *
 * Строка — общий `DocumentCard`: шторка узкая, и своя разметка здесь означала
 * бы третий вид карточки позиции в админке. Проблемная партия подсвечивается
 * тоном `danger` и объяснением под строкой: сервер атомарен, документ не
 * создан, и кладовщику важно видеть, из-за какой партии не прошло размещение.
 *
 * Поле ячейки появляется в строке только в режиме «по строкам»: в режиме
 * «одна ячейка для всех» повтор одного и того же адреса в каждой строке —
 * лишний шум и лишние тач-цели.
 */

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import { PlacementBinField } from "./PlacementBinField";
import { formatStockQty, stockLotText } from "./stock-level-format";
import type { PlacementMode } from "./stock-placement-payload";
import { DocumentCard } from "../../ui/DocumentCard";
import type { StorageBinDto } from "../../../lib/api-bins";
import { formatSkuName, type StockLevelDto } from "../../../lib/api-wms";

interface Props {
  rows: StockLevelDto[];
  mode: PlacementMode;
  bins: StorageBinDto[];
  /** Склад размещения — резолвер скана ячейки (phase19.22). */
  warehouseId: string;
  binByRow: Record<string, string>;
  onRowBin: (levelId: string, binId: string) => void;
  /** `lotId` → что не так с этой партией (ответ сервера). */
  errors: Record<string, string>;
  disabled?: boolean;
}

export function PlacementRows({
  rows,
  mode,
  bins,
  warehouseId,
  binByRow,
  onRowBin,
  errors,
  disabled = false,
}: Props) {
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((level) => {
        const error = level.lot ? errors[level.lot.id] : undefined;
        return (
          <li key={level.id}>
            <DocumentCard
              title={formatSkuName(level.sku)}
              code={level.sku?.code ?? "—"}
              qty={formatStockQty(level.quantity)}
              meta={[
                `${level.warehouse.name} · ${stockLotText(level)}`,
                error ? (
                  <span key="error" className="text-destructive">
                    {error}
                  </span>
                ) : null,
              ]}
              tone={error ? "danger" : "default"}
              actions={
                mode === "perRow" ? (
                  <div className="w-full">
                    <PlacementBinField
                      label={PLACEMENT_TEXT.rowBinLabel}
                      warehouseId={warehouseId}
                      bins={bins}
                      compact
                      value={binByRow[level.id] ?? ""}
                      onChange={(binId) => onRowBin(level.id, binId)}
                      disabled={disabled}
                    />
                  </div>
                ) : undefined
              }
            />
          </li>
        );
      })}
    </ul>
  );
}
